import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BsDatepickerModule, BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { NgSelectModule } from '@ng-select/ng-select';
import { AboutComponent } from './features/about/about.component';

import { NgHeaderComponent } from './shared/components/header/header.component';
import { NgHeaderMenuComponent } from './shared/components/header/header-menu.component';
import { BsDatepickerComponent } from './shared/components/datepickers/bs-datepicker/bs-datepicker.component';
import { NgTestComponent } from './features/ng-test/ng-test.component';
import { NgTimeComponent } from './features/ng-time/ng-time.component';
import { AppRoutingModule } from './app-routing.module';
import { FormsModule } from '@angular/forms';

import { AppComponent } from './app.component';
import { RootRedirectComponent } from './features/root-redirect/root-redirect.component';
import { DialogModalComponent } from './shared/components/modal/dialog-modal.component';
import { TimerecordsService } from './core/services/timerecords.service';
import { APP_CONSTANTS, APP_CONSTANTS_TOKEN } from './core/constants';

import { FlatpickrComponent } from './shared/components/datepickers/flatpickr/flatpickr.component';
import { AppDatepickerComponent } from './shared/components/datepickers/io-flatpickr/io-flatpickr.component';
import { ProjectSelectionModalComponent } from './shared/components/modal/project-selection-modal.component';
import { MomentPipe } from './shared/pipes/moment.pipe';
import { SwedishNumberPipe } from './shared/pipes/swedish-number.pipe';
import { SelectOnClickDirective } from './shared/directives/select-on-click.directive';
import { NumericHalfInputComponent } from './shared/components/numeric-input/numeric-half-input.component';
import { NgReportsComponent } from './features/ng-reports/ng-reports.component';
import { NgVacationComponent } from './features/ng-vacation/ng-vacation.component';
import { NgSalesReportsComponent } from './features/ng-sales-reports/ng-sales-reports.component';
import { NgProjectReportsComponent } from './features/ng-project-reports/ng-project-reports.component';
import { NgFixedPriceProjectsComponent } from './features/ng-fixed-price-projects/ng-fixed-price-projects.component';
import { NgProjectSummaryReportsComponent } from './features/ng-project-summary-reports/ng-project-summary-reports.component';
import { NgInvoiceComponent } from './features/ng-invoice/ng-invoice.component';
import { EditInvoiceModalComponent } from './shared/components/modal/edit-invoice-modal.component';
import { VacationService } from './core/services/vacation.service';
import { ErrorHandlingInterceptor } from './core/interceptors/error-handling.interceptor';
import { NgInvoiceReportsComponent } from './features/ng-invoice-reports/ng-invoice-reports.component';
import { NgTimeStatisticsReportComponent } from './features/ng-time-statistics-report/ng-time-statistics-report.component';
import { NgSalaryComponent } from './features/ng-salary/ng-salary.component';
import { TranslateStatusPipe } from './shared/pipes/translate-status.pipe';
import { NgLogoutComponent } from './features/ng-logout/ng-logout.component';
import { NgPasswordComponent } from './features/ng-password/ng-password.component';
import { NgLoginComponent } from './features/ng-login/ng-login.component';
import { NgLoginCallbackComponent } from './features/ng-login-callback/ng-login-callback.component';
import { NgAdminComponent } from './features/ng-admin/ng-admin.component';
import { AdminEmployeeTabComponent } from './features/ng-admin/admin-employee-tab/admin-employee-tab.component';
import { AdminCustomerTabComponent } from './features/ng-admin/admin-customer-tab/admin-customer-tab.component';
import { AdminProjectTabComponent } from './features/ng-admin/admin-project-tab/admin-project-tab.component';
import { AdminActivityTabComponent } from './features/ng-admin/admin-activity-tab/admin-activity-tab.component';
import { AdminActivityPriceTabComponent } from './features/ng-admin/admin-activity-price-tab/admin-activity-price-tab.component';
import { AdminTeamTabComponent } from './features/ng-admin/admin-team-tab/admin-team-tab.component';
import { AdminSalaryTabComponent } from './features/ng-admin/admin-salary-tab/admin-salary-tab.component';
import { AdminBulkCreateTabComponent } from './features/ng-admin/admin-bulk-create-tab/admin-bulk-create-tab.component';
import { AdminAuditTabComponent } from './features/ng-admin/admin-audit-tab/admin-audit-tab.component';
import { InternalTimeLockSummaryModalComponent } from './shared/components/modal/internal-time-lock-summary-modal.component';
import { HoursDrilldownModalComponent } from './shared/components/modal/hours-drilldown-modal.component';
import { FixedPriceProjectTimerecordsModalComponent } from './shared/components/modal/fixed-price-project-timerecords-modal.component';

@NgModule({
  imports: [
    BrowserModule,
    HttpClientModule,
    FormsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BsDatepickerModule.forRoot(),
    ButtonsModule.forRoot(),
    LoadingBarHttpClientModule,
    NgxChartsModule,
    NgSelectModule,
    FlatpickrComponent,
    AppDatepickerComponent,
    ProjectSelectionModalComponent,
    MomentPipe,
    TranslateStatusPipe,
  ],
  declarations: [
    RootRedirectComponent,
    DialogModalComponent,
    AppComponent,
    AboutComponent,
    NgHeaderComponent,
    NgHeaderMenuComponent,
    BsDatepickerComponent,
    NgTestComponent,
    NgTimeComponent,
    NgReportsComponent,
    NgVacationComponent,
    NgSalesReportsComponent,
    NgProjectReportsComponent,
    NgFixedPriceProjectsComponent,
    NgProjectSummaryReportsComponent,
    NgInvoiceComponent,
    EditInvoiceModalComponent,
    InternalTimeLockSummaryModalComponent,
    HoursDrilldownModalComponent,
    FixedPriceProjectTimerecordsModalComponent,
    NgInvoiceReportsComponent,
    NgTimeStatisticsReportComponent,
    NgSalaryComponent,
    SwedishNumberPipe,
    SelectOnClickDirective,
    NumericHalfInputComponent,
    NgLogoutComponent,
    NgPasswordComponent,
    NgLoginComponent,
    NgLoginCallbackComponent,
    NgAdminComponent,
    AdminEmployeeTabComponent,
    AdminCustomerTabComponent,
    AdminProjectTabComponent,
    AdminActivityTabComponent,
    AdminActivityPriceTabComponent,
    AdminTeamTabComponent,
    AdminSalaryTabComponent,
    AdminBulkCreateTabComponent,
    AdminAuditTabComponent,
  ],
  providers: [
    {
      provide: APP_CONSTANTS_TOKEN,
      useValue: APP_CONSTANTS,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlingInterceptor,
      multi: true,
    },
    BsDatepickerConfig,
    TimerecordsService,
    VacationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
